import { locateProxy } from '#~/agent/utils.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { home, quickShots } from '#~/control/movement/commands/index.js';
import { QuickShotSpeed } from '#~/control/movement/index.js';
import { raiseError } from '#~/notifications/index.js';
import { useRoom } from '#~/room/hooks.js';
import { Threshold } from './constants.js';
import type { Props, Wiring } from './types.js';
import { isCalibrating } from '@studio/models/control/calibration';
import { isEqual, Movement, Movements, Range, toPtz, Zero } from '@studio/models/ptz';
import type { PTZ } from '@studio/models/ptz';
import { useLatestFn, useTimeout } from '@studio/react-utils/hooks';
import type { T_Optional } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import { useState } from 'react';

/**
 * Quick shots wiring.
 *
 * @param props - Wiring input.
 * @param props.deviceId - Device ID.
 * @returns Quick shots wiring.
 */
export const useWiring = ({ deviceId }: Props): Wiring => {
  const [isStopping, setStopping] = useState(false);
  const [isHoming, setHoming] = useState(false);
  const timeout = useTimeout();

  const store = useRoom((µ) => {
    const { calibration, movement } = µ.devices[deviceId]?.control ?? {};

    const proxy = locateProxy(deviceId);

    const capabiltiies = proxy?.state.status?.device[deviceId]?.capabilities;
    const position = proxy?.state.status?.device[deviceId]?.movement?.position;
    const bCalibrating = isCalibrating(calibration?.status);

    return {
      capabilities: {
        [Movement.PanLeft]:
          !bCalibrating &&
          !isEqual(position?.pan ?? 0, Range.min, Threshold) &&
          !!capabiltiies?.movement?.vectors?.pan?.enabled,
        [Movement.PanRight]:
          !bCalibrating &&
          !isEqual(position?.pan ?? 0, Range.max, Threshold) &&
          !!capabiltiies?.movement?.vectors?.pan?.enabled,
        [Movement.TiltUp]:
          !bCalibrating &&
          !isEqual(position?.tilt ?? 0, Range.max, Threshold) &&
          !!capabiltiies?.movement?.vectors?.tilt?.enabled,
        [Movement.TiltDown]:
          !bCalibrating &&
          !isEqual(position?.tilt ?? 0, Range.min, Threshold) &&
          !!capabiltiies?.movement?.vectors?.tilt?.enabled,
        [Movement.ZoomIn]:
          !bCalibrating &&
          !isEqual(position?.zoom ?? 0, Range.max, Threshold) &&
          !!capabiltiies?.movement?.vectors?.zoom?.enabled,
        [Movement.ZoomOut]:
          !bCalibrating &&
          !isEqual(position?.zoom ?? -1, Zero.zoom, Threshold) &&
          !!capabiltiies?.movement?.vectors?.zoom?.enabled,
        home: !bCalibrating && !isHoming && !!capabiltiies?.movement?.canMoveHome
      },
      intent: toPtz(movement?.quickShots)
    };
  }, dequal);

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  const handleHome = useLatestFn(() => {
    if (!store.capabilities.home) {
      return;
    }

    home(deviceId)
      .then(() => {
        setHoming(true);

        timeout(() => {
          setHoming(false);
        }, 500);
      })
      .catch((error) => raiseError(error, { actionable: true }));
  });

  const handleMovement = useLatestFn((movement: Movement | false, isSelected: boolean = false) => {
    const nextIntent =
      movement === false
        ? Zero
        : ((): T_Optional<PTZ> => {
            const { axis } = Movements[movement];

            if (isSelected) {
              return {
                [axis]: 0
              };
            }

            return {
              [axis]: QuickShotSpeed * Math.sign(Movements[movement].ptz[axis])
            };
          })();

    if (movement !== false && isProtected) {
      triggerControlAlert?.(() => {
        quickShots(deviceId, nextIntent).catch((error) => raiseError(error, { actionable: true }));
      });

      return;
    }

    if (movement === false) {
      setStopping(true);

      setTimeout(() => {
        setStopping(false);
      }, 250);
    }

    quickShots(deviceId, nextIntent).catch((error) => raiseError(error, { actionable: true }));
  });

  return {
    ...store,
    handleHome,
    handleMovement,
    isHoming,
    isStopping
  };
};
